<script lang="ts">
    import { _ } from "svelte-i18n"
    import { kirbyFetch } from "../../api/kirby/api"
    import { getPageSelect } from "../../api/kirby/queries"
    import { spaLink } from "../../functionTools/routing"
    import { lang } from "../../store"
    import MoreButton from "./MoreButton.svelte"
    import SocialShare from "./SocialShare.svelte"

    let {
        type,
        title,
        referenceTitle,
        isArticle = false,
        page,
        content,
        article,
        width = "extended",
        padding = true,
        enabled = true,
        isPerson = false,
        styles = "",
        breadcrumbsForMobile = false,
    }: {
        type: "return" | "reference" | "title"
        width?: "normal" | "extended"
        styles?: string
        title?: string
        padding?: boolean
        isArticle?: boolean
        article?: KirbyPage<"any">
        referenceTitle: string
        page: string
        content: import("svelte").Snippet
        enabled?: boolean
        breadcrumbsForMobile?: boolean
        isPerson?: boolean
    } = $props()
    let innerWidth: number = $state(typeof window !== "undefined" ? window.innerWidth : 0)
    let isMobile = $derived(innerWidth < 1400)
    const breadcrumbPages: KirbyPage[] = $state([])
    async function getPagesByPath() {
        const path = typeof window !== "undefined" ? window.location.pathname : ""
        const relevantPath = path.split(`/${$lang}/`).pop()
        if (relevantPath && !breadcrumbPages.length) {
            const pathParts = relevantPath.split("/")
            const pagesPaths = []
            for (let i = 0; i < pathParts.length; i++) {
                const page = pathParts.slice(0, i + 1).join("/")
                pagesPaths.push(page)
            }
            if (pagesPaths.length < 2) return

            const pagePromises = pagesPaths.map((page, idx) =>
                kirbyFetch<KirbyPage<"any">>({
                    query: `page('${page}')`,
                    select: getPageSelect(),
                }).then((res) => ({ res, idx }))
            )

            const results = await Promise.all(pagePromises)
            results.sort((a, b) => a.idx - b.idx)
            breadcrumbPages.splice(0, breadcrumbPages.length)
            results.forEach(({ res }, i) => {
                if (i == 0 && isPerson) {
                    breadcrumbPages.push({
                        title: referenceTitle,
                        url: page,
                    })
                } else if (res) breadcrumbPages.push(res)
            })
        }
    }
    $effect(() => {
        if (isMobile) getPagesByPath()
    })
</script>

<svelte:window bind:innerWidth />
{#if enabled}
    <section style={styles}>
        <header class={width} class:padding>
            <div class="inner-header-wrapper">
                {#if isMobile && breadcrumbPages.length}
                    <ul>
                        {#each breadcrumbPages as page, idx}
                            <li>
                                <a use:spaLink href="/{$lang}/{page.url.split(`/${$lang}/`).pop()}">{page.title}</a>

                                {#if idx < breadcrumbPages.length - 1}
                                    <svg
                                        xmlns="http://www.w3.org/2000/svg"
                                        width="18"
                                        height="18"
                                        viewBox="0 0 18 18"
                                        fill="none"
                                    >
                                        <path
                                            d="M6.75 3.75L11.25 9L6.75 14.25"
                                            stroke="#242220"
                                            stroke-width="1.5"
                                            stroke-linecap="round"
                                            stroke-linejoin="round"
                                        ></path>
                                    </svg>
                                {/if}
                            </li>
                        {/each}
                    </ul>
                    <div class="page-dividor"></div>
                {:else if type == "title"}
                    <bold>{title}</bold>
                    <div class="page-dividor"></div>
                {:else if type == "return"}
                    <MoreButton text={referenceTitle} path={page} type="dark" reverseArrow={true} />
                    <div class="page-dividor"></div>
                    {#if isArticle && article}
                        <div class="share-row">
                            <small style="font-size:0.7rem;">{$_("general.share")}</small>
                            <div class="share-icons">
                                <SocialShare
                                    path="/{article.id}"
                                    teaser={article.subtitle}
                                    title={article.title}
                                    image={article.meta_img}
                                    hashtags={["#StiftungFamilienunternehmen"]}
                                />
                            </div>
                        </div>
                    {/if}
                {:else if type == "reference"}
                    <bold>{title}</bold>
                    <div class="page-dividor"></div>
                    <MoreButton text={referenceTitle} path={page} type="dark" shiftRight={true} />
                {/if}
            </div>
        </header>
        {@render content()}
    </section>
{:else}
    {@render content()}
{/if}

<style lang="less">
    @import "../../css/variables.less";
    section {
        display: flex;
        flex-direction: column;
        gap: 1.2rem;
        position: relative;
        align-items: center;
        width: 100%;

        header {
            position: sticky;
            z-index: 5;
            height: 4rem;
            top: calc(var(--header-height) - 20px);
            background-color: var(--bg-100);
            width: 100%;
            display: flex;
            justify-content: center;
            &.padding {
                padding: 0px var(--default-padding);
            }
            padding-top: 2.2rem;
            padding-bottom: 0.6rem;
            max-width: 100%;
            overflow: hidden;
            @media @max-mobile {
                margin-bottom: 0px;
                height: 6rem;
                top: calc(var(--header-height) - 40px);
            }
            .share-row {
                display: flex;
                gap: 0.6rem;
                align-items: center;
                text-align: center;
                small {
                    display: flex;
                    text-align: center;
                    align-items: center;

                    font-size: 0.5rem;
                    text-transform: uppercase;
                }
                .share-icons {
                    display: flex;
                    align-items: center;
                    gap: 0.3rem;
                }
            }
            .inner-header-wrapper {
                max-width: var(--extended-max-width);
                width: 100%;
                display: flex;
                gap: 0.6rem;
                align-items: center;
                @media @max-mobile {
                    padding: 0.6rem 0px;
                }
                & > ul {
                    display: flex;
                    gap: 0.3rem;
                    align-items: center;
                    * {
                        font-size: 0.8rem;
                        font-weight: bold;
                    }
                    svg {
                        height: 0.7rem;
                    }
                    flex-wrap: wrap;
                }
                bold {
                    text-transform: uppercase;
                    font-weight: 700;
                    @media @max-mobile {
                        font-size: 0.9rem;
                    }
                }
                .page-dividor {
                    flex: 1;
                    min-width: 30px;
                    height: 1px;
                    background: var(--dark-20);
                }
            }
            &.extended {
                .inner-header-wrapper {
                    max-width: var(--extended-max-width);
                }
            }
            &.normal {
                .inner-header-wrapper {
                    max-width: var(--normal-max-width);
                }
            }
        }
    }
</style>
